import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../scripts/AuthContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { cn } from "@/lib/utils";

/**
 * Inline image attachment in a workplace DM or partner bubble. Both require an
 * authenticated fetch, so we pull the blob and render it as an object URL
 * rather than using `<img src>` directly. DM paths take `org_id` as a query
 * param; partner paths carry the org in the path and pass no `orgId`.
 */
export default function DmImageAttachment({ attachment, orgId, className }) {
  const { accessToken } = useAuth();
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  const url = attachment?.type === "image" ? attachment.url : null;

  useEffect(() => {
    if (!url || !accessToken) return undefined;
    let cancelled = false;
    let objectUrl = null;
    setError(false);
    setSrc(null);
    axios
      .get(`${API_BASE_URL}${url}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: orgId ? { org_id: orgId } : undefined,
        responseType: "blob",
      })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, accessToken, orgId]);

  if (!url || error) return null;

  return (
    <div className={cn("mt-1 max-w-[280px] overflow-hidden rounded-md", className)}>
      {src ? (
        <img
          src={src}
          alt={attachment.file_name || "Image"}
          className="block w-full h-auto max-h-64 rounded-md object-contain"
          loading="lazy"
        />
      ) : (
        <div className="h-40 w-40 rounded-md bg-muted/40 animate-pulse" />
      )}
    </div>
  );
}
